import Sortable from 'sortablejs';
/**
 * Element Plus 表格列拖拽指令
 * 支持左右拖拽调整列顺序，可禁用特定列
 */
export const TableColumnDragDrop = {
  /**
   * 指令挂载时调用
   */
  mounted(el, binding) {
    // 解析配置
    const config = parseBinding(binding);
    
    // 验证和初始化
    if (validateConfig(config, el)) {
      initializeColumnDrag(el, config);
    }
  },
  
  /**
   * 组件更新时调用
   */
  updated(el, binding) {
    const oldConfig = el._columnDragConfig;
    const newConfig = parseBinding(binding);
    
    // 如果配置变化，重新初始化
    if (oldConfig && JSON.stringify(oldConfig) !== JSON.stringify(newConfig)) {
      cleanup(el);
      initializeColumnDrag(el, newConfig);
    }
  },
  
  /**
   * 指令卸载时调用
   */
  unmounted(el) {
    cleanup(el);
  }
};

/**
 * 解析指令绑定值
 */
function parseBinding(binding) {
  const defaultValue = {
    columns: [],           // 列配置数组
    onColumnChange: null,  // 列顺序变化回调
    disabledCondition: null, // 禁用条件
    disabledClass: 'no-column-drag', // 禁用列样式
    animation: 150,
    handle: '.column-drag-handle', // 拖拽手柄
    ghostClass: 'column-drag-ghost',
    dragClass: 'column-drag-drag',
    filterClass: 'filtered',
    preventOnFilter: true,
    allowCrossTable: false
  };
  
  // 处理不同类型的绑定值
  if (Array.isArray(binding.value)) {
    return { ...defaultValue, columns: binding.value };
  }
  
  if (typeof binding.value === 'object' && binding.value !== null) {
    return { ...defaultValue, ...binding.value };
  }
  
  return defaultValue;
}

/**
 * 验证配置
 */
function validateConfig(config, el) {
  if (!Array.isArray(config.columns)) {
    console.warn('[v-table-column-drag-drop] 必须提供 columns 参数（数组）');
    return false;
  }
  
  // 检查是否有表头
  const tableHeader = el.querySelector('.el-table__header-wrapper thead');
  if (!tableHeader) {
    console.warn('[v-table-column-drag-drop] 未找到表格头部元素');
    return false;
  }
  
  return true;
}

/**
 * 初始化列拖拽
 */
function initializeColumnDrag(el, config) {
  const tableHeader = el.querySelector('.el-table__header-wrapper thead');
  const headerRow = tableHeader.querySelector('tr');
  
  if (!headerRow) {
    console.warn('[v-table-column-drag-drop] 未找到表头行');
    return;
  }
  
  // 保存配置
  el._columnDragConfig = config;
  el._headerRow = headerRow;
  
  // 为表头单元格添加拖拽能力
  prepareHeaderCells(el, config);
  
  // 创建 Sortable 实例
  const sortable = createColumnSortable(headerRow, el, config);
  el._sortableInstance = sortable;
  
  // 初始化样式
  updateColumnStyles(el, config);
}

/**
 * 准备表头单元格
 */
function prepareHeaderCells(el, config) {
  const cells = el._headerRow.querySelectorAll('th');
  
  cells.forEach((cell, index) => {
    // 获取列数据
    const columnData = config.columns[index];
    if (!columnData) return;
    
    // 添加数据属性
    cell.setAttribute('data-column-index', index);
    cell.setAttribute('data-column-prop', columnData.prop || '');
    
    // 添加拖拽手柄（如果需要）
    if (!cell.querySelector('.column-drag-handle')) {
      const handle = createDragHandle(config);
      cell.appendChild(handle);
    }
    
    // 应用禁用样式
    if (config.disabledCondition && config.disabledCondition(columnData, index)) {
      cell.classList.add(config.disabledClass);
    }
  });
}

/**
 * 创建拖拽手柄
 */
function createDragHandle(config) {
  const handle = document.createElement('div');
  handle.className = config.handle.replace('.', '');
  handle.innerHTML = '⋮⋮';
  handle.style.cssText = `
    cursor: move;
    color: #c0c4cc;
    font-size: 12px;
    display: inline-block;
    margin-left: 5px;
    user-select: none;
  `;
  return handle;
}

/**
 * 创建列拖拽 Sortable 实例
 */
function createColumnSortable(headerRow, el, config) {
  const sortableConfig = {
    animation: config.animation,
    ghostClass: config.ghostClass,
    dragClass: config.dragClass,
    filter: `.${config.disabledClass}`,
    preventOnFilter: config.preventOnFilter,
    handle: config.handle,
    
    // 列拖拽特殊处理
    onStart: function(evt) {
      // 检查是否可拖拽
      if (isColumnDisabled(evt.item, el, config)) {
        evt.cancel();
        return false;
      }
      
      // 同步表格体
      syncBodyColumns(evt.oldIndex, evt.newIndex, el, 'start');
      return true;
    },
    
    onMove: function(evt) {
      return !isColumnDisabled(evt.dragged, el, config);
    },
    
    onEnd: function(evt) {
      handleColumnDragEnd(evt, el, config);
    }
  };
  
  return Sortable.create(headerRow, sortableConfig);
}

/**
 * 检查列是否被禁用
 */
function isColumnDisabled(columnElement, el, config) {
  if (!columnElement || !config.disabledCondition) {
    return false;
  }
  
  const columnData = getColumnData(columnElement, el, config);
  if (!columnData) return false;
  
  const cells = Array.from(el._headerRow.querySelectorAll('th'));
  const index = cells.indexOf(columnElement);
  
  return config.disabledCondition(columnData, index);
}

/**
 * 获取列数据
 */
function getColumnData(columnElement, el, config) {
  const cells = Array.from(el._headerRow.querySelectorAll('th'));
  const index = cells.indexOf(columnElement);
  
  return config.columns[index];
}

/**
 * 处理列拖拽结束
 */
function handleColumnDragEnd(evt, el, config) {
  const { oldIndex, newIndex } = evt;
  
  if (oldIndex === newIndex || oldIndex < 0 || newIndex < 0) {
    return;
  }
  
  const columns = config.columns;
  if (!Array.isArray(columns)) return;
  
  // 检查源列是否可拖拽
  const columnData = columns[oldIndex];
  if (config.disabledCondition && config.disabledCondition(columnData, oldIndex)) {
    return;
  }
  
  // 更新列顺序
  const item = columns.splice(oldIndex, 1)[0];
  columns.splice(newIndex, 0, item);
  
  // 同步表格体
  syncBodyColumns(oldIndex, newIndex, el, 'end');
  
  // 更新样式
  updateColumnStyles(el, config);
  
  // 触发回调
  if (typeof config.onColumnChange === 'function') {
    config.onColumnChange({
      oldIndex,
      newIndex,
      column: item,
      columns: [...columns]
    });
  }
}

/**
 * 同步表格体的列顺序
 * 关键：需要同时移动表体和表头的列
 */
function syncBodyColumns(oldIndex, newIndex, el, action = 'end') {
  const tableBody = el.querySelector('.el-table__body-wrapper tbody');
  if (!tableBody) return;
  
  const rows = tableBody.querySelectorAll('tr');
  
  rows.forEach(row => {
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length > Math.max(oldIndex, newIndex)) {
      if (action === 'start') {
        // 开始拖拽时，为动画做准备
        const cell = cells[oldIndex];
        cell.style.transition = 'transform 0.15s';
      } else {
        // 结束拖拽时，实际移动DOM元素
        const cell = cells[oldIndex];
        if (newIndex > oldIndex) {
          row.insertBefore(cell, cells[newIndex + 1]);
        } else {
          row.insertBefore(cell, cells[newIndex]);
        }
      }
    }
  });
}

/**
 * 更新列样式
 */
function updateColumnStyles(el, config) {
  const cells = el._headerRow.querySelectorAll('th');
  
  cells.forEach((cell, index) => {
    const columnData = config.columns[index];
    
    if (columnData && config.disabledCondition && config.disabledCondition(columnData, index)) {
      cell.classList.add(config.disabledClass);
      // 禁用拖拽手柄
      const handle = cell.querySelector('.column-drag-handle');
      if (handle) {
        handle.style.cursor = 'not-allowed';
        handle.style.opacity = '0.3';
      }
    } else {
      cell.classList.remove(config.disabledClass);
      // 启用拖拽手柄
      const handle = cell.querySelector('.column-drag-handle');
      if (handle) {
        handle.style.cursor = 'move';
        handle.style.opacity = '1';
      }
    }
  });
}

/**
 * 清理资源
 */
function cleanup(el) {
  if (el._sortableInstance) {
    el._sortableInstance.destroy();
    delete el._sortableInstance;
  }
  
  // 清理添加的拖拽手柄
  const handles = el.querySelectorAll('.column-drag-handle');
  handles.forEach(handle => handle.remove());
  
  delete el._columnDragConfig;
  delete el._headerRow;
}

/**
 * 安装函数
 */
export function installTableColumnDragDrop(app, options = {}) {
  const defaultOptions = {
    name: 'table-column-drag-drop',
    ...options
  };
  
  app.directive(defaultOptions.name, TableColumnDragDrop);
  
  app.config.globalProperties.$tableColumnDragDrop = {
    version: '1.0.0',
    installed: true
  };
}

export default TableColumnDragDrop;